import { useState } from "react";
import { Dialog, DialogActions, DialogContent, Menu, MenuItem, Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, FoodsTooltip, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useDeleteActionPlanTemplate } from "src/api/client-api/actionPlanTemplates/useActionPlanTemplate";
import type { ActionPlanTemplate } from "src/api/client-api/actionPlanTemplates/useActionPlanTemplates";
import { useSnackbar } from "src/utils/useSnackbar";
import { STATUS_ACTIONS, canUseTemplateAction, type TemplateMenuAction } from "./templateMenu";

import { ArchiveTemplateDialog } from "./ArchiveTemplateDialog";
import { useTemplateLifecycleActions } from "./useTemplateLifecycleActions";
import { PublishTemplateDialog } from "./PublishTemplateDialog";
import { useTemplatePublicationBlocker } from "./useTemplatePublicationBlocker";

type TemplateActionsMenuProps = {
    template: Pick<
        ActionPlanTemplate,
        "id" | "name" | "ssqId" | "sectorId" | "status" | "actionCount" | "canView" | "canModify" | "canDelete"
    >;
    canCreate: boolean;
    onView?: () => void;
    onEdit?: () => void;
    onDeleted?: () => void;
};

export const TemplateActionsMenu = (props: TemplateActionsMenuProps) => {
    const { template, canCreate, onView, onEdit, onDeleted } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const snackbar = useSnackbar();
    const mutation = useDeleteActionPlanTemplate();
    const lifecycle = useTemplateLifecycleActions(template.id);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [publishOpen, setPublishOpen] = useState(false);
    const publicationBlocker = useTemplatePublicationBlocker(template);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const actions = STATUS_ACTIONS[template.status].filter(
        (action) =>
            canUseTemplateAction(action, template, canCreate) &&
            (onView !== undefined || ["duplicate", "archive", "delete"].includes(action)),
    );
    const icons = {
        view: "viewPwd",
        edit: "edit",
        duplicate: "duplicate",
        publish: "importUpload",
        archive: "archive",
        unarchive: "importUpload",
        delete: "delete",
    } as const;
    const labels: Record<TemplateMenuAction, string> = {
        view: t("settings.action_plan_templates.action.view", "View"),
        edit: t("settings.action_plan_templates.action.edit", "Edit"),
        duplicate: t("settings.action_plan_templates.action.duplicate", "Duplicate"),
        publish: t("settings.action_plan_templates.action.publish", "Publish"),
        archive: t("settings.action_plan_templates.action.archive", "Archive"),
        unarchive: t("settings.action_plan_templates.action.unarchive", "Unarchive"),
        delete: t("settings.action_plan_templates.action.delete", "Delete"),
    };
    const closeDelete = () => {
        if (!mutation.isLoading) setConfirmDelete(false);
    };
    if (actions.length === 0) return null;

    return (
        <>
            <Button
                variant="secondary"
                disabled={lifecycle.isLoading}
                sx={{ minWidth: theme.spacing(5), width: theme.spacing(5), p: 0, color: "text.primary" }}
                aria-label={t("settings.action_plan_templates.open_menu", "Actions for {{name}}", {
                    name: template.name,
                })}
                aria-haspopup="menu"
                aria-expanded={anchor !== null}
                onClick={(event) => {
                    event.stopPropagation();
                    setAnchor(event.currentTarget);
                }}
            >
                ⋯
            </Button>
            <Menu
                anchorEl={anchor}
                open={anchor !== null}
                onClose={() => setAnchor(null)}
                MenuListProps={{ disabledItemsFocusable: true }}
                onClick={(event) => event.stopPropagation()}
            >
                {actions.map((action) => {
                    const enabled =
                        action === "view" ||
                        action === "delete" ||
                        action === "archive" ||
                        action === "unarchive" ||
                        action === "duplicate" ||
                        (action === "edit" && onEdit !== undefined) ||
                        (action === "publish" && !publicationBlocker);
                    const disabledReason =
                        action === "publish" ? publicationBlocker : (
                            t("settings.action_plan_templates.action_unavailable", "This action is not available yet.")
                        );
                    return (
                        <FoodsTooltip key={`${action}-template-menu`} title={enabled ? "" : disabledReason}>
                            <MenuItem
                                aria-label={labels[action]}
                                aria-disabled={!enabled}
                                sx={{
                                    gap: theme.spacing(2),
                                    ...(!enabled ? { color: theme.palette.text.disabled, cursor: "default" } : {}),
                                }}
                                onClick={() => {
                                    if (!enabled) return;
                                    setAnchor(null);
                                    if (action === "view") onView?.();
                                    if (action === "edit") onEdit?.();
                                    if (action === "publish") setPublishOpen(true);
                                    if (action === "archive") setArchiveOpen(true);
                                    if (action === "unarchive" || action === "duplicate") lifecycle.execute(action);
                                    if (action === "delete") {
                                        mutation.reset();
                                        setConfirmDelete(true);
                                    }
                                }}
                            >
                                <FoodsIcon icon={icons[action]} size={1.5} />
                                <Typography>{labels[action]}</Typography>
                            </MenuItem>
                        </FoodsTooltip>
                    );
                })}
            </Menu>
            {archiveOpen && <ArchiveTemplateDialog templateId={template.id} onClose={() => setArchiveOpen(false)} />}
            {publishOpen && <PublishTemplateDialog templateId={template.id} onClose={() => setPublishOpen(false)} />}
            <Dialog
                open={confirmDelete}
                onClose={closeDelete}
                aria-labelledby={`delete-template-${template.id}`}
                fullWidth
                maxWidth="sm"
                onClick={(event) => event.stopPropagation()}
            >
                <Stack sx={{ px: theme.spacing(4), pt: theme.spacing(4) }}>
                    <Typography id={`delete-template-${template.id}`} variant="h3">
                        {t("settings.action_plan_templates.delete.dialog_title", "Delete this template?")}
                    </Typography>
                </Stack>
                <DialogContent sx={{ px: theme.spacing(4), pt: theme.spacing(2), pb: theme.spacing(4) }}>
                    <Typography component="p" variant="body-regular">
                        {template.actionCount === 1 ?
                            t(
                                "settings.action_plan_templates.delete.confirm_single_action",
                                '"{{name}}" and its action will be permanently deleted. This cannot be undone. Action plans already created by users are not affected.',
                                { name: template.name },
                            )
                        :   t(
                                "settings.action_plan_templates.delete.confirm_multiple_actions",
                                '"{{name}}" and its {{total}} actions will be permanently deleted. This cannot be undone. Action plans already created by users are not affected.',
                                { name: template.name, total: template.actionCount },
                            )
                        }
                    </Typography>
                    {mutation.isError && (
                        <Typography component="p" variant="body-regular" role="alert">
                            {t(
                                "settings.action_plan_templates.delete.failed",
                                "Unable to delete the template. Please try again.",
                            )}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        p: theme.spacing(4),
                        borderTop: 1,
                        borderColor: "divider",
                        flexDirection: "column",
                        gap: theme.spacing(2),
                        "& > :not(style) ~ :not(style)": { marginLeft: 0 },
                    }}
                >
                    <Button
                        variant="delete"
                        fullWidth
                        disabled={mutation.isLoading}
                        onClick={() => {
                            if (mutation.isLoading) return;
                            mutation.mutate(template.id, {
                                onSuccess: () => {
                                    setConfirmDelete(false);
                                    snackbar.setSnackbarProps({
                                        type: "success",
                                        message: t(
                                            "settings.action_plan_templates.delete.success",
                                            '"{{name}}" has been deleted.',
                                            { name: template.name },
                                        ),
                                    });
                                    onDeleted?.();
                                },
                            });
                        }}
                    >
                        {t("settings.action_plan_templates.delete.yes", "Yes, delete")}
                    </Button>
                    <Button fullWidth variant="secondary" disabled={mutation.isLoading} onClick={closeDelete}>
                        {t("settings.action_plan_templates.delete.no", "No, cancel")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
